// Contact dialogs - All German
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const ANREDE_OPTIONS = ['Herr', 'Frau'];
const GESCHLECHT_OPTIONS = ['M', 'F'];
const SPRACHE_OPTIONS = ['DE', 'FR', 'IT', 'EN'];
const ALTERSKATEGORIE_OPTIONS = ['U12', 'U14', 'U16', 'U18', 'Aktive', 'Senior'];

// Dialog for Vitalis contacts (formerly "Base Centre")
export function ContactCentreDialog({ open, onClose, contact, setContact, tags, onSave }) {
  if (!contact) return null;
  
  const toggleTag = (tagName) => {
    const ct = contact.tags || [];
    setContact({ ...contact, tags: ct.includes(tagName) ? ct.filter(x => x !== tagName) : [...ct, tagName] });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{contact.id ? 'Kontakt bearbeiten' : 'Neuer Kontakt'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Anrede</Label>
            <Select value={contact.anrede || contact.civilite || ''} onValueChange={v => setContact({ ...contact, anrede: v })}>
              <SelectTrigger data-testid="contact-centre-anrede">
                <SelectValue placeholder="Auswählen..." />
              </SelectTrigger>
              <SelectContent>
                {ANREDE_OPTIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Nachname</Label><Input value={contact.nachname || contact.nom || ''} onChange={e => setContact({ ...contact, nachname: e.target.value })} data-testid="contact-centre-nachname" /></div>
            <div><Label>Vorname</Label><Input value={contact.vorname || contact.prenom || ''} onChange={e => setContact({ ...contact, vorname: e.target.value })} data-testid="contact-centre-vorname" /></div>
          </div>
          <div><Label>Email</Label><Input type="email" value={contact.email || ''} onChange={e => setContact({ ...contact, email: e.target.value })} data-testid="contact-centre-email" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Telefon</Label><Input value={contact.telefon || contact.telephone || ''} onChange={e => setContact({ ...contact, telefon: e.target.value })} data-testid="contact-centre-telefon" /></div>
            <div><Label>Geburtsdatum</Label><Input type="date" value={contact.geburtsdatum || contact.date_naissance || ''} onChange={e => setContact({ ...contact, geburtsdatum: e.target.value })} data-testid="contact-centre-geburtsdatum" /></div>
          </div>
          <div>
            <Label>Klassierung</Label>
            <Input 
              value={contact.klassierung || contact.classement_tennis || ''} 
              onChange={e => setContact({ ...contact, klassierung: e.target.value })} 
              placeholder="z.B. R4, N3..."
              data-testid="contact-centre-klassierung"
            />
          </div>
          <div>
            <Label>Tags</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {tags.map(t => (
                <Badge key={t.id} style={{ backgroundColor: contact.tags?.includes(t.name) ? t.color : '#e5e7eb' }} className={`cursor-pointer ${contact.tags?.includes(t.name) ? 'text-white' : 'text-gray-600'}`} onClick={() => toggleTag(t.name)}>{t.name}</Badge>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2"><Checkbox checked={contact.disabled} onCheckedChange={ch => setContact({ ...contact, disabled: ch })} /><Label>Deaktiviert</Label></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Abbrechen</Button><Button onClick={onSave}>Speichern</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Dialog for Swiss Tennis contacts
export function ContactProjetDialog({ open, onClose, contact, setContact, onSave }) {
  if (!contact) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{contact.id ? 'Kontakt bearbeiten' : 'Neuer Kontakt'} - Swiss Tennis</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Anrede</Label>
              <Select value={contact.anrede || ''} onValueChange={v => setContact({ ...contact, anrede: v })}>
                <SelectTrigger data-testid="contact-projet-anrede">
                  <SelectValue placeholder="Auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {ANREDE_OPTIONS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Geschlecht</Label>
              <Select value={contact.geschlecht || ''} onValueChange={v => setContact({ ...contact, geschlecht: v })}>
                <SelectTrigger data-testid="contact-projet-geschlecht">
                  <SelectValue placeholder="Auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {GESCHLECHT_OPTIONS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Nachname</Label><Input value={contact.nachname || contact.nom || ''} onChange={e => setContact({ ...contact, nachname: e.target.value })} data-testid="contact-projet-nachname" /></div>
            <div><Label>Vorname</Label><Input value={contact.vorname || contact.prenom || ''} onChange={e => setContact({ ...contact, vorname: e.target.value })} data-testid="contact-projet-vorname" /></div>
          </div>
          <div><Label>Email</Label><Input type="email" value={contact.email || ''} onChange={e => setContact({ ...contact, email: e.target.value })} data-testid="contact-projet-email" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Sprache</Label>
              <Select value={contact.sprache || ''} onValueChange={v => setContact({ ...contact, sprache: v })}>
                <SelectTrigger data-testid="contact-projet-sprache">
                  <SelectValue placeholder="Auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {SPRACHE_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Klassierung</Label>
              <Input 
                value={contact.klassierung || ''} 
                onChange={e => setContact({ ...contact, klassierung: e.target.value })} 
                placeholder="z.B. R4, N3..."
                data-testid="contact-projet-klassierung"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Alterskategorie</Label>
              <Select value={contact.alterskategorie || ''} onValueChange={v => setContact({ ...contact, alterskategorie: v })}>
                <SelectTrigger data-testid="contact-projet-alterskategorie">
                  <SelectValue placeholder="Auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {ALTERSKATEGORIE_OPTIONS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Regionalverband</Label>
              <Input 
                value={contact.regionalverband || ''} 
                onChange={e => setContact({ ...contact, regionalverband: e.target.value })} 
                placeholder="z.B. Tennis Aargau..."
                data-testid="contact-projet-regionalverband"
              />
            </div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Abbrechen</Button><Button onClick={onSave}>Speichern</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
